<template>
  <div
    :class="getItemTableContainerClass"
  >
    <ItemTable
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      bulkActionText="run now"
      :columns="columns"
      :details="inMainPanel && !isCommandRunner"
      :handleBulkAction="handleBulkAction"
      :handleBulkDelete="handleBulkDelete"
      :handleDetailsButtonClick="handleDetailsButtonClick"
      :handleRowClick="handleRowClick"
      :hasSelectColumn="true"
      :inDetailsAsidePanel="!inMainPanel"
      :isAsideOpen="isAsideOpen"
      :itemType="itemType"
      :keepFresh="inMainPanel"
      :mainActionMenuItems="mainActionMenuItems"
      :route="route"
      :selectedModelId="selectedModelId"
      :selectionItemText="{
        label: 'selected',
        plural: 'commands',
        singular: 'command',
      }"
      :setGettingStartedTooltipVisibility="setGettingStartedTooltipVisibility"
      :setSelectedModelId="setSelectedModelId"
      :showBound="false"
      :showBulkAction="true"
      :showBulkDelete="!isCommandRunner"
      :showMainAction="!isCommandRunner"
      :showSearchForm="true"
      :useDropDownFilterComponent="useDropDownFilterComponent"
      :viewport="viewport"
    >
      <template
        #row-overlay="slotProps"
      >
        <div
          v-show="commandRunningIds[slotProps.item.id]"
          :class="$style.overlay"
        >
          <HeadingMedium
            :class="$style.overlayText"
            :hasMargin="false"
            weight="bold"
          >
            Command Running...
          </HeadingMedium>
          <ButtonGroup :class="$style.cancelSection">
            <Button
              :class="$style.cancelIconButton"
              hasOutline
              text="Cancel Command"
              type="error"
              @click.native="cancelRunningCommand($event, slotProps.item.id)"
            />
          </ButtonGroup>
        </div>
      </template>
    </ItemTable>
  </div>
</template>

<script>
import * as Constants from 'client/util/Constants';
import {
  Button,
  ButtonGroup,
  HeadingMedium,
} from '@jumpcloud/ui-components';
import { eventLocations, eventProperties, events } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { jcGrey600 } from 'client/css/brand-colors.css';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import EventManager from 'client/GrowthEngineering/EventManager';
import FeatureTrialsMixin from 'client/GrowthEngineering/utilities/FeatureTrialsMixin';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import LaunchSummaryText from 'client/Commands/LaunchSummaryText';
import ModalEventBus from 'components/Modal/ModalEventBus';
import async from 'async';
import launchTypes from 'client/Commands/constants/LaunchTypes';
import pluralize from 'pluralize';

import IconLaunchTypeAddUser from 'public/img/icons/command-launch-type-add-user.svg?inline';
import IconLaunchTypeDeleteUser from 'public/img/icons/command-launch-type-delete-user.svg?inline';
import IconLaunchTypeEveryLogin from 'public/img/icons/command-launch-type-every-login.svg?inline';
import IconLaunchTypeManual from 'public/img/icons/command-launch-type-manual.svg?inline';
import IconLaunchTypeNextLogin from 'public/img/icons/command-launch-type-next-login.svg?inline';
import IconLaunchTypeOneTime from 'public/img/icons/command-launch-type-one-time.svg?inline';
import IconLaunchTypeRegister from 'public/img/icons/command-launch-type-register.svg?inline';
import IconLaunchTypeRepeated from 'public/img/icons/command-launch-type-repeated.svg?inline';
import IconLaunchTypeTrigger from 'public/img/icons/command-launch-type-trigger.svg?inline';

const { ItemTableConsts, ItemTypeConsts, StoreModuleMap } = Constants;
const { error, success } = Constants.default;
const commandCollection = StoreModuleMap[ItemTypeConsts.command];

const {
  description,
  location,
  text,
  toUrl,
} = eventProperties;

const {
  commandsPage,
} = eventLocations;

// Legacy aside panel events.
// This can be removed when the commands aside panel has been refactored.
const legacyAsidePanelCanceledEventName = 'command-details:canceled';
const legacyAsidePanelSavedEventName = 'command-details:saved';
const legacyCommandsDeletedEventName = 'commands:deleted';

const iconProps = {
  fill: jcGrey600,
  height: '18px',
  style: {
    left: '1px',
    marginRight: '10px',
    position: 'relative',
    verticalAlign: '-5%',
  },
  width: '18px',
};

export default {
  name: 'CommandItemTable',

  components: {
    Button,
    ButtonGroup,
    HeadingMedium,
    ItemTable,
  },

  mixins: [
    ItemTableComputedMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
    FeatureTrialsMixin,
  ],

  commandFromTemplateText: 'Command From Template',

  data() {
    return {
      columns: [
        {
          bodyComponent: 'ItemName',
          bodyComponentProps: {
            iconComponent: item => this.getLaunchTypeIconComponent(item),
            titleField: 'name',
            subtitleFunction: LaunchSummaryText.getLaunchSummaryText,
          },
          classNames: {
            body: [this.$style.commandNameColumn],
            heading: [this.$style.commandNameColumn],
          },
          headingText: 'Name',
          sortBy: 'name',
        },
        {
          classNames: {
            body: [this.$style.codeColumn, this.$style.scriptColumn],
            heading: [this.$style.scriptColumn],
          },
          dataField: 'command',
          headingText: 'Command',
          sortBy: 'command',
        },
        {
          bodyComponent: 'CommandOSIcon',
          headingText: 'OS',
          sortBy: 'commandType',
        },
        {
          bodyComponent: 'ActionCommandButton',
          bodyComponentProps: {
            buttonText: 'Run Now',
            handleClick: this.handleRunCommandClick,
            buttonType: 'run',
          },
          classNames: {
            body: [this.$style.commandItemRightActionButton],
          },
        },
        {
          bodyComponent: 'ActionCommandButton',
          bodyComponentProps: {
            buttonText: 'Delete',
            handleClick: this.handleDeleteCommandClick,
            buttonType: 'delete',
          },
          classNames: {
            body: [this.$style.commandItemRightActionButton],
          },
        },
      ],
      commandRunningIds: {},
      itemType: ItemTypeConsts.command,
      panelType: ItemTableConsts.panelType.mainPanel,
      route: '/#/commands',
    };
  },

  computed: {
    ...mapGetters(commandCollection, {
      selectedItems: 'getSelectedItems',
    }),

    ...mapGetters('SettingsModule', {
      isCommandRunner: 'isCommandRunner',
    }),

    mainActionMenuItems() {
      const mainActionMenuItems = [
        {
          clickHandler: this.handelOpenNewCommandClick,
          componentProps: iconProps,
          text: 'Command',
        },
        {
          clickHandler: this.handleTemplateAction,
          componentProps: iconProps,
          text: this.$options.commandFromTemplateText,
        },
      ];

      if (this.$hasFeatureFlag(this.$featureFlags.commandTriggerRegistration)) {
        mainActionMenuItems.push({
          clickHandler: this.handleRegistrationTriggerAction,
          componentProps: iconProps,
          text: 'Command After Agent Install',
        });
      }

      return mainActionMenuItems;
    },
  },

  mounted() {
    if (this.viewport) {
      const { refresh } = this.$refs.itemTable;
      this.viewport.on(legacyAsidePanelCanceledEventName, refresh);
      this.viewport.on(legacyAsidePanelSavedEventName, refresh);
    }
  },

  beforeDestroy() {
    clearTimeout(this.commandCheckerTimeout);
  },

  destroyed() {
    if (this.viewport) {
      this.viewport.off(legacyAsidePanelCanceledEventName);
      this.viewport.off(legacyAsidePanelSavedEventName);
    }
  },

  methods: {
    ...mapActions(commandCollection, [
      'cancelQueuedCommands',
      'checkQueuedCommands',
      'deleteItems',
      'itemLaunchType',
      'runCommand',
    ]),

    ...mapGetters(commandCollection, [
      'getItem',
    ]),

    addRunningOverlay(id) {
      this.$set(this.commandRunningIds, id, true);
    },

    cancelRunningCommand(e, id) {
      e.stopPropagation();
      return this.cancelQueuedCommands(id)
        .then((count) => {
          // Handle cancel response...
          if (count) {
            notify(success, `Successfully canceled ${count} queued commands.`);
          } else {
            notify(error, 'No commands were queued.');
          }
        })
        .catch(() => {
          notify(error, 'An error occurred when trying to cancel the queued commands.');
        })
        .finally(() => {
          this.removeRunningOverlay(id);
        });
    },

    deleteCommandById(id) {
      const componentProps = {
        bodyText: 'Are you sure you would like to delete this command?',
        continueText: 'yes, delete',
        handleContinue: () => {
          componentProps.loading = true;
          this.handleContinue([id]);
        },
        headerText: 'Delete Command',
        loading: false,
        mainActionAttention: true,
      };

      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps,
      });
    },

    getLaunchTypeIconComponent(item) {
      let iconType;

      switch (item.launchType) {
        case launchTypes.addUser:
          iconType = IconLaunchTypeAddUser;
          break;
        case launchTypes.deleteUser:
          iconType = IconLaunchTypeDeleteUser;
          break;
        case launchTypes.everyLogin:
          iconType = IconLaunchTypeEveryLogin;
          break;
        case launchTypes.manual:
          iconType = IconLaunchTypeManual;
          break;
        case launchTypes.nextLogin:
          iconType = IconLaunchTypeNextLogin;
          break;
        case launchTypes.oneTime:
          iconType = IconLaunchTypeOneTime;
          break;
        case launchTypes.nextRegistration:
          iconType = IconLaunchTypeRegister;
          break;
        case launchTypes.repeated:
          iconType = IconLaunchTypeRepeated;
          break;
        case launchTypes.trigger:
          iconType = IconLaunchTypeTrigger;
          break;
        default:
          iconType = '';
      }

      return iconType;
    },

    handelOpenNewCommandClick() {
      this.$router.push('/commands/new');
      const { triggerEvent } = EventManager;
      triggerEvent(events.addNewCommandButtonClicked);
    },

    handleBulkAction() {
      async.each(this.selectedItems, this.runCommandById);
    },

    handleBulkDelete() {
      const componentProps = {
        bodyText: 'Are you sure you would like to delete these commands?',
        continueText: 'yes, delete',
        handleContinue: () => {
          componentProps.loading = true;
          this.handleContinue(this.selectedItems);
        },
        headerText: 'Delete Command(s)',
        loading: false,
        mainActionAttention: true,
      };

      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps,
      });
    },

    handleContinue(items) {
      this.deleteItems(items)
        .then((result) => {
          const { deletedIds, failedTasks } = result;
          if (failedTasks.length > 0) {
            notify(error, `The selected ${pluralize(`${this.itemType}`, failedTasks.length)} could not be deleted.`);
          } else {
            notify(success, `${pluralize('Command', deletedIds.length)} successfully deleted.`);
            if (this.viewport) {
              this.viewport.trigger(legacyCommandsDeletedEventName);
            }
          }
        })
        .finally(() => {
          ModalEventBus.close();
        });
    },

    handleDeleteCommandClick(e, item) {
      e.stopPropagation();
      this.deleteCommandById(item.id);
    },

    async handleDetailsButtonClick(itemId) {
      let route = `${this.route}/${itemId}/details`;
      if (this.inMainPanel && !this.isCommandRunner) {
        const launchType = await this.itemLaunchType(itemId);
        if (launchType === launchTypes.nextRegistration) {
          route = `${this.route}/registrationtrigger/${itemId}/details`;
        }
        this.$router.push(route);
      }
    },

    handleMainAction() {
      this.handelOpenNewCommandClick();
    },

    handleRegistrationTriggerAction() {
      this.$router.push('/commands/registrationtrigger');
    },

    handleRunCommandClick(e, item) {
      e.stopPropagation();
      this.runCommandById(item.id);
    },

    handleTemplateAction() {
      const commandsFromTemplateRoute = '/commands/newfromtemplate';
      this.$router.push(commandsFromTemplateRoute);

      const { triggerButtonClicked } = EventHelper;

      triggerButtonClicked({
        [description]: 'opens new commands template page',
        [location]: commandsPage,
        [text]: this.$options.commandFromTemplateText,
        [toUrl]: commandsFromTemplateRoute,
      });
    },

    removeRunningOverlay(id) {
      this.$delete(this.commandRunningIds, id);
    },

    runCommandById(id) {
      this.addRunningOverlay(id);
      return this.runCommand(id)
        .then((queueIds) => {
          if (queueIds && queueIds.length) {
            // Start the timeout to check for queued commands...
            this.startCheckQueuedCommandsTimeout();
          } else {
            // Remove overlay if no queue
            this.removeRunningOverlay(id);
          }
        })
        .catch((err) => {
          this.removeRunningOverlay(id);
          notify(error, err.message);
        });
    },

    startCheckQueuedCommandsTimeout() {
      clearTimeout(this.commandCheckerTimeout);

      this.commandCheckerTimeout = setTimeout(() => {
        this.checkQueuedCommands()
          .then((commandIdsRemovedFromQueue) => {
            commandIdsRemovedFromQueue.forEach((commandId) => {
              this.removeRunningOverlay(commandId);
            });

            // Run the checker again if we're still waiting for commands to run...
            if (Object.keys(this.commandRunningIds).length) {
              this.startCheckQueuedCommandsTimeout();
            }
          })
          .catch(() => {
            notify(error, 'There was an issue checking queued commands');
            Object.keys(this.commandRunningIds).forEach((commandId) => {
              this.removeRunningOverlay(commandId);
            });
          });
      }, 5000);
    },
  },
};
</script>

<style module>
:global(.item-list.with-results) {
  height: 50%;
}

.codeColumn {
  font-family: var(--jc-font-monospace);
  font-size: 10px;
}

.commandNameColumn { max-width: 325px; }

.scriptColumn {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.overlay {
  background-color: rgba(51, 51, 51, 0.8);
  height: 100%;
  position: absolute;
  right: -3rem;
  text-align: center;
  width: 120%;
}

.overlayText {
  color: var(--jcWhite);
  height: 100%;
  margin-top: 1em !important;
}

.cancelSection {
  position: absolute;
  right: 9em;
  top: 17px;
}
.cancelSection:focus { outline: none; }

.cancelIconButton {
  width: 16em;
}

.commandItemRightActionButton {
  width: 10em;
}
</style>
